import DataTable, { Column } from "components/DataTable";
import { StatusChip } from "components/StatusChip";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { conductLegislativeDrafting } from "types";
import TabNav from "../../components/TabNav";
import PageHeader from "components/PageHeader";
import { useAuth } from "contexts/AuthContext";
import Button from "components/buttons";
import { getLegislativeDraftings } from "services/legislativeService";
import { LEGAL_TRAINING_PROVISION_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { useToast } from "contexts/ToastContext";
import { getEmployeesByAdminAndOrg, getStakeholder } from "services/getEmployees";
import { getLegalTrainingProvisions, getLegalTrainingProvisionsZone, getLegalTrainingProvisionsZoneByAdminUnit } from "services/legalTrainingProvision";
import { getLegalAwarenessZone, getLegalAwarenessZoneByAdminUnit } from "services/legalAwarenessService";
import { useTranslation } from "react-i18next";

const LegalAwarenessCreationZone = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const contextData = useAuth();
    const [activeTab, setActiveTab] = useState<string>("All");
    const tabs = [t("All") || "All", t("Internal") || "Internal", t("NewRequest") || "New Request"];
    const [data, setData] = useState<{ isLoading: boolean, data: conductLegislativeDrafting[] }>({ isLoading: true, data: [] });
    const [filteredData, setFilteredData] = useState<{ isLoading: boolean, data: conductLegislativeDrafting[] }>({ isLoading: true, data: [] });
    const [tabFilteredData, setTabFilteredData] = useState<{ isLoading: boolean, data: conductLegislativeDrafting[] }>({ isLoading: true, data: [] });
    const { showToast } = useToast();

    const columns: Column<conductLegislativeDrafting>[] = [
        {
            header: t("Zone") || "Zone",
            accessor: "admin_unit_name",
            render: (row: any) => (
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#0A1D39] text-white flex items-center justify-center text-xs font-bold">
                        {String(row?.admin_unit_name || "?").trim().charAt(0).toUpperCase()}
                    </div>
                    <p className="font-medium text-[#0A1D39]">{row?.admin_unit_name || "-"}</p>
                </div>
            ),
            widthClass: "w-1/3",
            searchValue: (item: any) => String(item?.admin_unit_name || ""),
        },
        {
            header: t("SentDate") || "Sent Date",
            accessor: "sent_date",
            render: (row: any) => <p>{row?.sent_date ? String(row.sent_date).split("T")[0] : "-"}</p>,
        },
        {
            header: t("ReportStatus") || "Report Status",
            accessor: "report_summary",
            render: (row: any) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${row?.report_summary ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${row?.report_summary ? "bg-green-500" : "bg-amber-500"}`} />
                    {row?.report_summary ? (t("Submitted") || "Submitted") : (t("NotSubmitted") || "Not Submitted")}
                </span>
            ),
        },
        {
            header: t("ViewAction") || "View Action",
            accessor: "action",
            render: (row: any) => (
                <div className="flex gap-2 cursor-pointer" onClick={() => { navigate(`${row?.id}`) }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="22" viewBox="0 0 17 22" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M10 0V5.5C10 5.89782 10.158 6.27936 10.4393 6.56066C10.7206 6.84196 11.1022 7 11.5 7H17V14C17 14.5304 16.7893 15.0391 16.4142 15.4142C16.0391 15.7893 15.5304 16 15 16H13V18C13 18.5304 12.7893 19.0391 12.4142 19.4142C12.0391 19.7893 11.5304 20 11 20H2C1.46957 20 0.960859 19.7893 0.585786 19.4142C0.210714 19.0391 0 18.5304 0 18V6C0 5.46957 0.210714 4.96086 0.585786 4.58579C0.960859 4.21071 1.46957 4 2 4H4V2C4 1.46957 4.21071 0.960859 4.58579 0.585786C4.96086 0.210714 5.46957 0 6 0H10ZM4 6H2V18H11V16H6C5.46957 16 4.96086 15.7893 4.58579 15.4142C4.21071 15.0391 4 14.5304 4 14V6ZM12 0.043C12.3789 0.123355 12.7263 0.311992 13 0.586L16.414 4C16.688 4.27373 16.8766 4.62112 16.957 5H12V0.043Z" fill="#212121" />
                    </svg>
                    <svg className="my-auto" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <mask id="mask0_9_31087" maskUnits="userSpaceOnUse" x="1" y="5" width="22" height="14">
                            <path d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z" fill="white" stroke="white" strokeWidth="1.33333" strokeLinejoin="round" />
                            <path d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z" fill="black" stroke="black" strokeWidth="1.33333" strokeLinejoin="round" />
                        </mask>
                        <g mask="url(#mask0_9_31087)">
                            <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                        </g>
                    </svg>
                </div>
            ),
        },
    ];

    useEffect(() => {
        getLegalAwarenessZoneByAdminUnit(contextData.user.admin_unit || "")
            .then((data) => {
                setData((prev) => {
                    return { ...prev, data: data }
                });
                console.log(data);
            })
            .catch((error) => {
                console.error("Error fetching legal research requests:", error);
                showToast(t("FailedToFetchLegalResearchRequest") || "Failed to fetch legal research request.", "error");
            })
            .finally(() => {
                setData((prev) => ({
                    ...prev,
                    isLoading: false,
                }));
            });
    }, [getLegislativeDraftings, setData, showToast, t, contextData.user.admin_unit]);

    useEffect(() => {
        if (!data?.data) return;

        Promise.all(
            data.data.map(async (research) => {
                if (!research.requested_by_id) return research;

                let user;
                if (research.requested_by === "stakeholder" && research.requested_by_id)
                    user = await getStakeholder(research.requested_by_id, contextData.token || contextData?.user?.access_token || "")
                else {
                    try {
                        const users = await getEmployeesByAdminAndOrg({
                            bearer:
                                contextData.token ??
                                contextData?.user?.access_token ??
                                contextData?.user.user?.access_token,
                            org_unit: contextData?.user?.org_unit || "",
                            admin_unit: contextData?.user?.admin_unit || "",
                        });
                        const match = users.find((u: { roles: string[], user: any }) => u.user.id === research.requested_by_id);
                        user = match?.user;
                    } catch (error) {
                        console.error(error);
                    }
                }
                return {
                    ...research,
                    requested_by_user: user,
                };
            })
        ).then((enriched) => {
            setFilteredData({ isLoading: false, data: enriched.filter(Boolean) as conductLegislativeDrafting[] });
        });
    }, [data, contextData]);

    useEffect(() => { console.log(data.data); }, [data]);
    
    useEffect(() => {
        let tabData;
        if (activeTab === t("NewRequest") || activeTab === "New Request") {
            tabData = filteredData.data.filter(
                (req: any) =>
                    req.status === LEGAL_TRAINING_PROVISION_REQUEST_STATUS.submitted.value
            );
        }
        else if (activeTab === t("Internal") || activeTab === "Internal")
            tabData = filteredData.data.filter((req: any) =>
                req.status === LEGAL_TRAINING_PROVISION_REQUEST_STATUS.submitted_internal.value
            );
        else
            tabData = filteredData.data

        setTabFilteredData({
            isLoading: false,
            data: tabData || []
        });
    }, [activeTab, filteredData.data, t]);

    return (
        <div className="mx-auto flex flex-col space-y-4">
            <PageHeader title={t("LegalAwareness") || "Legal Awareness"} />
            <div className="flex">
                <div className="mt-auto">
                    <TabNav active={activeTab} tabs={tabs} onSelect={setActiveTab} />
                </div>
                {contextData.user.user_type == "external" &&
                    <div className="ml-auto">
                        <Button
                            onClick={() => navigate("request")}
                            variant="primary"
                            type="button"
                        >
                            {t("CreateNewRequest") || "Create New Request"}
                        </Button>
                    </div>
                }
                {contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") &&
                    <div className="ml-auto">
                        <Button
                            onClick={() => navigate("internal-request")}
                            variant="primary"
                            type="button"
                        >
                            {t("CreateNewRequest") || "Create New Request"}
                        </Button>
                    </div>
                }
            </div>

            <DataTable
                title={`< ${t("LegalAwarenessTable") || "Legal Awareness Table"}`}
                columns={columns}
                data={tabFilteredData.data}
                rowKey={(row) => row.id}
                onSearch={(q) => { }}
                pagination={true}
            />
        </div>
    );
};

export default LegalAwarenessCreationZone;